<template>
  <nx-fullscreen-dialog
      :active="active"
      :title="$t('message.block.form.image.label')"
      @dismiss="dismiss">

    <template slot="toolbar-items">
      <v-btn
          :disabled="!isValid"
          @click="insertBlot"
          color="accent"
          flat
          v-html="$t('message.block.form.image.accept')"
      ></v-btn>
    </template>

    <v-container pb-3 fluid>
      <v-layout>
        <v-flex class="md8 offset-md2 sm10 offset-sm1 pt-4">
          <v-layout row wrap>

            <v-flex class="xs12">
              <h2 class="nx-block-headline" v-html="$t('message.block.form.title')"></h2>
              <v-select
                  :items="options"
                  :label="$t('message.block.form.image.type.label')"
                  v-model="data.type"
              ></v-select>
              <v-text-field
                  :error-messages="errors"
                  :label="$t('message.block.form.image.external.label')"
                  :placeholder="$t('message.block.form.image.external.placeholder')"
                  autofocus
                  v-model="data.src"
                  v-show="data.type === ScrollytellingImageEmbed.TYPE.EXTERNAL"
              ></v-text-field>
            </v-flex>

            <v-flex class="md6 xs12 order-xs2 order-sm1 pr-3">
              <nx-clipping-form-board-selector
                  name="clipping"
                  :label="$t('message.block.form.image.content.desktopLabel')"
                  :aspect-ratios="availableClippingAspectRatioAliases"
                  :value="clipping"
                  @input="updateClipping"
                  v-show="data.type === ScrollytellingImageEmbed.TYPE.INTERNAL"
              ></nx-clipping-form-board-selector>
              <nx-image
                  :src="data.src || null"
                  v-show="data.type === ScrollytellingImageEmbed.TYPE.EXTERNAL && data.src"
                  @load="previewLoaded(true)"
                  @error="previewLoaded(false)"
              ></nx-image>
              <nx-form-file-image-selector
                name="fileId"
                label="Desktop multimedia (image/mp4)"
                :manual-hint-text="$t('message.upload.scrollytellingSelectFile')"
                :before-upload-clippings-config="['source-aspect-ratio']"
                :filters="{mimeType: 'video/mp4, image/*', collectionId}"
                :value="data.fileId"
                @input="updateFileId"
                v-show="data.type === ScrollytellingImageEmbed.TYPE.COLLECTION"
              />
            </v-flex>
            <v-flex class="md6 xs12 order-xs2 order-sm1 pr-3">
              <nx-clipping-form-board-selector
                  name="mobileClipping"
                  :label="$t('message.block.form.image.content.mobileLabel')"
                  :aspect-ratios="availableClippingAspectRatioAliases"
                  :value="mobileClipping"
                  @input="updateMobileClipping"
                  v-show="data.type === ScrollytellingImageEmbed.TYPE.INTERNAL"
              ></nx-clipping-form-board-selector>
              <nx-form-file-image-selector
                name="mobileFileId"
                label="Mobile multimedia (image/mp4)"
                :manual-hint-text="$t('message.upload.scrollytellingSelectFile')"
                :before-upload-clippings-config="['source-aspect-ratio']"
                :filters="{mimeType: 'video/mp4, image/*', collectionId}"
                :value="data.mobileFileId"
                @input="updateMobileFileId"
                v-show="data.type === ScrollytellingImageEmbed.TYPE.COLLECTION"
              />
            </v-flex>

            <v-flex class="xs12 order-xs1 order-sm2">
              <v-text-field
                :label="$t('message.form.fields.imageAlt')"
                v-model="data.alt"
                :hint="$t('message.form.fields.imageAltHint')"
                persistent-hint
              ></v-text-field>
              <v-text-field
                  :label="$t('message.block.form.image.caption.label')"
                  v-model="data.caption"
              ></v-text-field>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>

  </nx-fullscreen-dialog>
</template>

<script>
import api from '@/modules/clipping/api';
import NxClippingFormBoardSelector from '@/modules/clipping/form/BoardSelector';
import { mapGetters } from 'vuex';
import DialogButton from './DialogButton';
import NxImage from '../../../media/Image';
import NxFormFileImageSelector from '@/modules/file/form/FileImageSelector.vue';
import ScrollytellingImageEmbed from '@/components/texteditor/advanced/blots/scrollytellingImageEmbed';

/**
 * ImageButton Component
 *
 * Extend DialogButton component
 *
 */
export default {
  name: 'nx-scrollytelling-image-button',
  components: {
    NxImage,
    NxClippingFormBoardSelector,
    NxFormFileImageSelector,
  },
  extends: DialogButton,
  props: {
    embedAlt: String,
    embedCaption: String,
    embedClippingId: String,
    embedMobileClippingId: String,
    embedSrc: String,
    embedType: String,
    embedFileId: String,
    embedFileSrc: String,
    embedMobileFileId: String,
    embedMobileFileSrc: String,
  },
  data() {
    return {
      clipping: null,
      mobileClipping: null,
      fileId: null,
      fileSrc: null,
      mobileFileId: null,
      mobileFileSrc: null,
      data: {
        alt: '',
        caption: '',
        clippingId: null,
        mobileClippingId: null,
        src: '',
        type: ScrollytellingImageEmbed.TYPE.INTERNAL,
        fileId: null,
        fileSrc: '',
        mobileFileId: null,
        mobileFileSrc: '',
      },
      errors: [],
      ScrollytellingImageEmbed,
      name: 'scrollytellingImageEmbed',
      options: [
        {
          text: this.$t(`message.block.form.image.type.${ScrollytellingImageEmbed.TYPE.EXTERNAL}`),
          value: ScrollytellingImageEmbed.TYPE.EXTERNAL,
        },
        {
          text: this.$t(`message.block.form.image.type.${ScrollytellingImageEmbed.TYPE.INTERNAL}`),
          value: ScrollytellingImageEmbed.TYPE.INTERNAL,
        },
        {
          text: this.$t(`message.block.form.image.type.${ScrollytellingImageEmbed.TYPE.COLLECTION}`),
          value: ScrollytellingImageEmbed.TYPE.COLLECTION,
        },
      ],
    };
  },
  computed: {
    ...mapGetters(['availableClippingAspectRatioAliases']),
    ...mapGetters('scrollytelling', [
      'collectionId',
    ]),
    blockTitle() {
      return this.$t('message.block.photo');
    },
    isValid() {
      if (this.data.type === ScrollytellingImageEmbed.TYPE.EXTERNAL) {
        return this.data.src !== '' && this.errors.length === 0;
      }
      if (this.data.type === ScrollytellingImageEmbed.TYPE.INTERNAL) {
        return !!this.data.clippingId;
      }
      if (this.data.type === ScrollytellingImageEmbed.TYPE.COLLECTION) {
        return !!this.data.fileId;
      }
      return false;
    },
  },
  methods: {
    cleanupDialog() {
      this.data = {
        alt: '',
        caption: '',
        clippingId: null,
        mobileClippingId: null,
        src: '',
        type: ScrollytellingImageEmbed.TYPE.EXTERNAL,
        fileId: null,
        fileSrc: null,
        mobileFileId: null,
        mobileFileSrc: null,
      };
    },
    updateFileId(fileId, valueAsObject) {
      if (!fileId) {
        this.fileId = null;
        this.fileSrc = null;
        this.data.fileId = null;
        this.data.fileSrc = null;
        this.data.mobileFileId = null;
        this.data.mobileFileSrc = null;
      }

      this.fileId = fileId;
      this.fileSrc = valueAsObject.publicUrl;
      this.data.fileId = fileId;
      this.data.fileSrc = valueAsObject.publicUrl;

      if (this.data.caption === '') {
        this.data.caption = valueAsObject.name;
      }
      if (this.data.alt === '') {
        this.data.alt = valueAsObject.name;
      }

      if (this.data.mobileFileId === null) {
        this.updateMobileFileId(fileId, valueAsObject);
      }
    },
    updateMobileFileId(fileId, valueAsObject) {
      if (!fileId) {
        this.mobileFileId = null;
        this.mobileFileSrc = null;
        this.data.fileId = null;
        this.data.fileSrc = null;
        this.data.mobileFileId = null;
        this.data.mobileFileSrc = null;
      }

      this.mobileFileId = fileId;
      this.mobileFileSrc = valueAsObject.publicUrl;
      this.data.mobileFileId = fileId;
      this.data.mobileFileSrc = valueAsObject.publicUrl;

      if (this.data.caption === '') {
        this.data.caption = valueAsObject.name;
      }
      if (this.data.alt === '') {
        this.data.alt = valueAsObject.name;
      }

      this.data.mobileFileId = fileId;
      if (this.data.fileId === null) {
        this.updateFileId(fileId);
      }
    },
    updateClipping(clipping) {
      // Update the caption if it's empty o it's the title of the previously selected clipping
      if (!clipping) {
        this.clipping = null;
        this.data.clippingId = null;
        this.data.caption = null;
        this.data.src = null;
        this.mobileClipping = null;
        this.data.mobileClippingId = null;
        return;
      }
      if (this.data.caption === ''
        || (this.clipping
          && this.clipping.image
          && this.data.caption === this.clipping.image.title)) {
        this.data.caption = clipping.image.title;
      }
      if (this.data.alt === ''
        || (this.clipping
          && this.clipping.image
          && this.data.alt === this.clipping.image.title)) {
        this.data.alt = clipping.image.title;
      }
      this.clipping = clipping;
      this.data.clippingId = clipping.id;
      this.data.src = clipping.src;
      if (this.mobileClipping === null) {
        this.updateMobileClipping(clipping);
      }
    },
    updateMobileClipping(clipping) {
      // Update the caption if it's empty o it's the title of the previously selected clipping
      if (!clipping) {
        this.clipping = null;
        this.data.clippingId = null;
        this.data.caption = null;
        this.data.src = null;
        this.mobileClipping = null;
        this.data.mobileClippingId = null;
        return;
      }
      if (this.data.caption === ''
        || (this.mobileClipping
          && this.mobileClipping.image
          && this.data.caption === this.mobileClipping.image.title)) {
        this.data.caption = clipping.image.title;
      }
      if (this.data.alt === ''
        || (this.mobileClipping
          && this.mobileClipping.image
          && this.data.alt === this.mobileClipping.image.title)) {
        this.data.alt = clipping.image.title;
      }
      this.mobileClipping = clipping;
      this.data.mobileClippingId = clipping.id;
      this.data.src = clipping.src;
      if (this.clipping === null) {
        this.updateClipping(clipping);
      }
    },
    async fetchClipping() {
      this.clipping = await api.getClipping(this.data.clippingId);
    },
    async fetchMobileClipping() {
      this.mobileClipping = await api.getClipping(this.data.mobileClippingId);
    },
    previewLoaded(withoutErrors) {
      if (withoutErrors) this.errors = [];
      else this.errors.push(this.$t('message.block.form.image.external.error'));
    },
  },
  mounted() {
    this.data = {
      alt: this.embedAlt || '',
      caption: this.embedCaption || '',
      clippingId: this.embedClippingId || '',
      mobileClippingId: this.embedMobileClippingId || '',
      src: this.embedSrc || '',
      type: this.embedType || ScrollytellingImageEmbed.TYPE.INTERNAL,
      fileId: this.embedFileId || null,
      fileSrc: this.embedFileSrc || null,
      mobileFileId: this.embedMobileFileId || null,
      mobileFileSrc: this.embedMobileFileSrc || null,
    };
    if (this.data.type === ScrollytellingImageEmbed.TYPE.INTERNAL
      && this.data.clippingId !== '') {
      this.fetchClipping();
    }
    if (this.data.type === ScrollytellingImageEmbed.TYPE.INTERNAL
      && this.data.mobileClippingId !== '') {
      this.fetchMobileClipping();
    }
  },
};
</script>

<style lang="stylus">
#image-preview {
  img {
    display: table;
    margin: auto;
  }
}
</style>
